import { takeLatest, put, call, select } from 'redux-saga/effects';
import get from 'lodash/get';
import { sha256 } from 'js-sha256';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { createAccount } from 'yoda-interfaces/lib/Account/AccountApi';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import setUserName from 'yoda-account-components/lib/common/setUserName';
import { getAccertifyDeviceInfo, logExtendedAnalytics } from './SiteProbingSaga';
import * as SignInActionTypes from '../actionTypes/SigninActionTypes';
import * as CreateAccountActionTypes from '../actionTypes/CreateAccountActionTypes';
import * as SignInSliderActionTypes from '../actionTypes/SignInSliderActionTypes';
import getBBid from '../helpers/Utils/bbid';
import AnalyticsActionTypes from '../actionTypes/AnalyticsActionTypes';
import { ANALYTICS_SRC_CODE } from '../common/Constants';

export function* createAccountSaga(actionParam) {
    const action = actionParam;
    if (TokenProvider.get('ACCOUNT_ID')) {
        action.payload.guestAccountId = TokenProvider.get('ACCOUNT_ID');
    }
    delete action.payload.featureFlags;
    if (action.payload.keepMeLoggedIn) {
        const customHeaders = getBBid(__SERVER__, action.payload.keepMeLoggedIn);
        action.payload.customHeaders = customHeaders;
    }
    delete action.payload.keepMeLoggedIn;
    let locationName = '';
    if (window?.location?.pathname?.startsWith('/cart/signin')) {
        locationName = 'CART';
    }
    if (window?.location?.pathname?.startsWith('/createaccount')) {
        locationName = 'CAM';
    }
    if (action.payload.isSignInSliderReq) {
        locationName = 'SLIDER';
    }
    const deviceInfo = yield call(getAccertifyDeviceInfo, {
        actionName: `${locationName}_create_account`,
    });
    if (deviceInfo) {
        action.payload.deviceInfo = deviceInfo;
    }
    const response = yield call(createAccount, action.payload);
    const isSuccess = response.status === 200 || response.status === 201;
    const payload = {
        response: response.data,
        isSuccess,
    };

    /*  Trigger ADI analytics only if we find accertify flag enabled and
        accertify header present as part of response */
    const appContext = yield select((state) => state.context);
    const { enableAccertifyADI } = (appContext || {}).featureFlags || {};
    const accertifyStatus = response?.headers?.get('x-adi-sts');
    if (enableAccertifyADI && ['A', 'R'].includes(accertifyStatus)) {
        yield call(logExtendedAnalytics, {
            actionMethod: `${locationName}_CREATE_ACCOUNT`,
            result: { response },
            isTriggerFromLS: isSuccess && !action.payload.isSignInSliderReq,
        });
    }

    const isMonitoringMode = response?.headers?.get('x-adi-monitor');
    /*  formError event was already triggered from inside logExtendedAnalytics
        in the case of enforced ADI Reject. */
    const isFormErrorEventTriggered =
        enableAccertifyADI && accertifyStatus === 'R' && !isMonitoringMode;
    if (!isSuccess && !isFormErrorEventTriggered) {
        const errorType = response.data?.errorCode === 'GENERIC_ERROR' ? 'SYSTEM' : 'BUSINESS';
        const src = ANALYTICS_SRC_CODE[`${locationName}_CREATE_ACCOUNT_${errorType}`];
        yield put({
            type: AnalyticsActionTypes.FORM_ERROR,
            errorDetails: {
                error: {
                    errorCategory: '',
                    errorDescription: response.data?.errorMessage,
                    errorID: response.data?.errorCode,
                },
                fraudForceErrData: { src },
            },
        });
    }

    payload.done = true;
    if (isSuccess) {
        const userEmail = get(action, 'payload.emailAddress', '');
        TokenProvider.set('DP_USER_EMAIL', userEmail);
        TokenProvider.set('DP_USER_EMAIL_HASHED', sha256(userEmail));
        response.userState = '1';
        TokenProvider.storeTokens(response, 'header', true);

        setUserName('DP_USER_NAME');
        setUserName('DP_USER_INFO');
        setUserName('USER_ACCT_ID');
        setUserName('DYN_USER_ID');
        if (TokenProvider.get('DP_SAVED_ITEMS_PAYLOAD')) {
            const data = JSON.parse(TokenProvider.get('DP_SAVED_ITEMS_PAYLOAD'));
            yield put({
                type: SignInActionTypes.SAVED_ITEMS_MERGE,
                data,
                isSignInSliderReq: action.payload.isSignInSliderReq,
                payloadData: payload,
                source: 'createaccount',
            });
            LocalStorage.removeData('DP_SAVED_ITEMS_PAYLOAD');
        } else if (action.payload.isSignInSliderReq) {
            payload.accountCreatedFromOCP = !!window?.location?.pathname?.startsWith(
                '/checkout/oc'
            );
            yield put({
                type: SignInSliderActionTypes.SIGNIN_SLIDER_CREATE_ACCOUNT_SUCCESS,
                payload,
            });
        } else {
            yield put({ type: CreateAccountActionTypes.CREATE_ACCOUNT_SUCCESS, payload });
        }
        // SIGN IN SLIDER
    } else if (action.payload.isSignInSliderReq) {
        yield put({
            type: SignInSliderActionTypes.SIGNIN_SLIDER_CREATE_ACCOUNT_ERROR,
            payload,
        });
    } else {
        yield put({
            type: CreateAccountActionTypes.CREATE_ACCOUNT_ERROR,
            payload,
        });
    }
}

function* watchCreateAccountSaga() {
    yield takeLatest(CreateAccountActionTypes.CREATE_ACCOUNT, createAccountSaga);
}
watchCreateAccountSaga.sagaName = 'createAccountSaga';
export default watchCreateAccountSaga;
